import { useState, useEffect } from 'react';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { Calendar, Lock, Unlock } from 'lucide-react';

export function FiscalSettingsPage() {
  const [years, setYears] = useState<any[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/accounting/org/fiscal')
      .then((res) => res.json())
      .then((data) => {
        if (data.ok || data.success) {
          setYears(data.data?.years || []);
          setPeriods(data.data?.periods || []);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <SettingsSubNav />

      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        <div>
          <h2 className="text-lg font-bold">Fiscal Years & Period Management</h2>
          <p className="text-xs text-slate-500">Configure monthly accounting periods, fiscal year rollover, and period locking controls.</p>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading fiscal configuration...</div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 space-y-4">
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm space-y-3">
                  <h3 className="text-sm font-bold flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-indigo-500" /> Fiscal Years
                  </h3>
                  <div className="space-y-2">
                    {years.map((y) => (
                      <div key={y._id} className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl flex justify-between items-center text-xs">
                        <div>
                          <div className="font-bold text-slate-900 dark:text-slate-100">{y.name}</div>
                          <div className="text-[10px] text-slate-500">
                            {new Date(y.startDate).toLocaleDateString('en-IN')} — {new Date(y.endDate).toLocaleDateString('en-IN')}
                          </div>
                        </div>
                        <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-success/10 text-success-text dark:bg-success dark:text-success-text">
                          {y.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm space-y-4">
                <h3 className="text-sm font-bold">Monthly Accounting Periods & Lock Date</h3>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {periods.map((p) => (
                    <div key={p._id} className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl flex items-center justify-between text-xs">
                      <div>
                        <div className="font-bold text-slate-900 dark:text-slate-100">{p.name}</div>
                        <div className="text-[10px] text-slate-500">
                          {new Date(p.startDate).toLocaleDateString('en-IN')} to {new Date(p.endDate).toLocaleDateString('en-IN')}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2.5 py-0.5 rounded text-[10px] uppercase font-bold flex items-center gap-1 ${
                            p.status === 'closed'
                              ? 'bg-danger/10 text-danger-text dark:bg-danger dark:text-danger-text'
                              : 'bg-success/10 text-success-text dark:bg-success dark:text-success-text'
                          }`}
                        >
                          {p.status === 'closed' ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                          {p.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Financial Policies Section */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-6">
              <h3 className="text-sm font-bold border-b border-slate-200 dark:border-slate-800 pb-3">Fiscal Year & Policy Controls</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Financial Year</label>
                  <select defaultValue="2026-27" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-mono">
                    <option value="2026-27">2026-2027</option>
                    <option value="2025-26">2025-2026</option>
                    <option value="2026-CY">CY 2026</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Start Month</label>
                  <select defaultValue="04" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                    <option value="04">April</option>
                    <option value="01">January</option>
                    <option value="07">July</option>
                    <option value="10">October</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">End Month</label>
                  <select defaultValue="03" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                    <option value="03">March</option>
                    <option value="12">December</option>
                    <option value="06">June</option>
                    <option value="09">September</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Accounting Lock Date</label>
                  <input
                    type="date"
                    defaultValue="2026-03-31"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Transaction Edit Window (Days)</label>
                  <input
                    type="number"
                    defaultValue={30}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Negative Stock Policy</label>
                  <select defaultValue="block" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                    <option value="allow">Allow Negative Stock</option>
                    <option value="warn">Warn User</option>
                    <option value="block">Block Transaction</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Credit Limit Policy</label>
                  <select defaultValue="warn" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                    <option value="warn">Warn on Breach</option>
                    <option value="require_approval">Require Manager Approval</option>
                    <option value="block">Block Order</option>
                  </select>
                </div>
              </div>

              <div className="p-4 bg-warning/10 dark:bg-warning border border-warning/25 dark:border-warning/40 rounded-xl flex items-center justify-between text-xs text-warning-text dark:text-warning-text">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-warning-text" />
                  <div>
                    <span className="font-bold">Stock Costing Policy: FIFO</span>
                    <span className="ml-2 opacity-80">(Locked once live inventory postings exist)</span>
                  </div>
                </div>
                <span className="font-bold bg-warning/15 dark:bg-warning px-2 py-0.5 rounded text-[10px] uppercase">🔒 Locked</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FiscalSettingsPage;
